/**
 * Shell – root authenticated layout.
 * Manages the WebSocket connection, notification drawer, and renders Side + Top nav.
 */
import { useState, useEffect, useCallback, Suspense } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import ErrorBoundary from '@/components/ErrorBoundary'
import PageLoader from '@/components/ui/PageLoader'
import { useAuth } from '@/hooks/useAuth'
import { useCapabilities } from '@/hooks/useCapabilities'
import OnboardingDisclaimerModal from '@/components/OnboardingDisclaimerModal'
import SideNav from './SideNav'
import TopNav from './TopNav'
import { useRealtime, useRealtimeStore } from '@/hooks/useRealtime'
import NotificationDrawer, { NotificationBell, NotificationDropdown } from '@/components/ui/NotificationDrawer'
import CommandPalette from '@/components/ui/CommandPalette'
import HelpPanel from '@/components/ui/HelpPanel'
import QuickTradeDialog from '@/components/ui/QuickTradeDialog'
import PriceTicker from '@/components/ui/PriceTicker'
import { useNotificationPolling } from '@/hooks/useNotifications'
import { Wifi, WifiOff, Search, Sun, Moon, HelpCircle, Zap } from 'lucide-react'
import { useRealtimePortfolio } from '@/hooks/useRealtime'
import FreshnessIndicator from '@/components/ui/FreshnessIndicator'

// ── Theme toggle ─────────────────────────────────────────────────────────────
function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const stored = localStorage.getItem('av-theme')
    return (stored === 'light' ? 'light' : 'dark') as 'dark' | 'light'
  })

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
    }
    localStorage.setItem('av-theme', theme)
  }, [theme])

  const toggle = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))
  return { theme, toggle }
}

function ThemeToggle() {
  const { theme, toggle } = useTheme()
  return (
    <button
      type="button"
      onClick={toggle}
      className="btn-ghost p-1.5 rounded-lg"
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
    >
      {theme === 'dark'
        ? <Sun size={15} aria-hidden />
        : <Moon size={15} aria-hidden />
      }
    </button>
  )
}

function getMarketSession(): { label: string; color: string } {
  const now = new Date()
  const nyHour = Number(
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/New_York' }).format(now)
  )
  const nyMin = Number(
    new Intl.DateTimeFormat('en-US', { minute: 'numeric', timeZone: 'America/New_York' }).format(now)
  )
  const day = new Date(
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York' }).format(now)
  ).getDay()
  const totalMin = nyHour * 60 + nyMin

  if (day === 0 || day === 6) return { label: 'MARKET CLOSED', color: '#f43f5e' }
  if (totalMin < 4 * 60) return { label: 'AFTER HOURS', color: '#f59e0b' }
  if (totalMin < 9 * 60 + 30) return { label: 'PRE-MARKET', color: '#f59e0b' }
  if (totalMin < 16 * 60) return { label: 'MARKET OPEN', color: '#10b981' }
  if (totalMin < 20 * 60) return { label: 'AFTER HOURS', color: '#f59e0b' }
  return { label: 'MARKET CLOSED', color: '#f43f5e' }
}

function getLondonTime(): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: 'Europe/London',
  }).format(new Date())
}

function LivePortfolioTicker() {
  const { value, dailyChangePct } = useRealtimePortfolio()
  if (!value) return null
  const pos = (dailyChangePct ?? 0) >= 0
  return (
    <div className="hidden lg:flex items-center gap-3 text-xs">
      <span className="text-text-muted">AUM</span>
      <span className="font-mono font-semibold text-text-primary">
        £{value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
      </span>
      <span className={pos ? 'text-emerald' : 'text-rose-light'} aria-live="polite">
        {pos ? '+' : ''}{(dailyChangePct ?? 0).toFixed(3)}%
      </span>
    </div>
  )
}

function MarketSessionIndicator() {
  const [session, setSession] = useState(getMarketSession)
  const [londonTime, setLondonTime] = useState(getLondonTime)

  useEffect(() => {
    const interval = setInterval(() => {
      setSession(getMarketSession())
      setLondonTime(getLondonTime())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="font-mono text-text-muted">{londonTime} LDN</span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: session.color }}
        />
        <span className="font-semibold tracking-wider" style={{ color: session.color }}>
          {session.label}
        </span>
      </span>
    </div>
  )
}

// ── Live Market Clock Bar ─────────────────────────────────────────────────────

interface MarketCenter {
  label: string
  tz: string
  openHour: number   // local hour (24h) market opens
  openMin: number
  closeHour: number  // local hour (24h) market closes
  closeMin: number
}

const MARKET_CENTERS: MarketCenter[] = [
  { label: 'Tokyo',    tz: 'Asia/Tokyo',           openHour: 9,  openMin: 0,  closeHour: 15, closeMin: 30 },
  { label: 'London',   tz: 'Europe/London',       openHour: 8,  openMin: 0,  closeHour: 16, closeMin: 30 },
  { label: 'New York', tz: 'America/New_York',     openHour: 9,  openMin: 30, closeHour: 16, closeMin: 0  },
]

function getClockInfo(center: MarketCenter, now: Date): { time: string; open: boolean } {
  const fmt = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: center.tz }).format(now)

  const time = fmt({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })

  // Determine day-of-week in that TZ
  const dayStr = fmt({ weekday: 'short' })
  const isWeekend = dayStr === 'Sat' || dayStr === 'Sun'

  const localHour = Number(fmt({ hour: 'numeric', hour12: false }))
  const localMin  = Number(new Intl.DateTimeFormat('en-GB', { minute: 'numeric', timeZone: center.tz }).format(now))
  const totalMin  = localHour * 60 + localMin
  const openMin   = center.openHour * 60 + center.openMin
  const closeMin  = center.closeHour * 60 + center.closeMin

  const open = !isWeekend && totalMin >= openMin && totalMin < closeMin
  return { time, open }
}

function MarketClockBar() {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000)
    return () => clearInterval(id)
  }, [])

  return (
    <div
      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-center
                 sm:gap-6 px-4 py-1 sm:py-1
                 bg-black/20 border-b border-white/5 text-[10px] sm:text-[11px] font-mono"
      aria-label="Live market clocks"
    >
      {MARKET_CENTERS.map((center, i) => {
        const { time, open } = getClockInfo(center, now)
        return (
          <span key={center.label} className="flex items-center gap-1.5 text-text-muted py-0.5 sm:py-0">
            {i > 0 && <span className="hidden sm:inline text-white/10 select-none mx-1">|</span>}
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${open ? 'bg-emerald-400' : 'bg-rose-500'}`}
              aria-hidden
            />
            <span className="text-text-secondary">{center.label}</span>
            <span className="tabular-nums">{time}</span>
            {!open && (
              <span className="hidden sm:inline text-text-muted opacity-60">
                ({center.label === 'New York' ? `opens ${String(center.openHour).padStart(2,'0')}:${String(center.openMin).padStart(2,'0')}` : 'closed'})
              </span>
            )}
            <span className={`sm:hidden text-text-muted opacity-60 ${open ? 'text-emerald-400' : ''}`}>
              {open ? '· open' : '· closed'}
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ── Split floating action button — Help (left half) + Quick Trade (right half)
// Auto-hides when an input/textarea is focused so it never blocks in-place
// row edits (e.g. editing a cash value on the Accounts page).
function SplitFAB({ onHelp, onTrade }: { onHelp: () => void; onTrade: () => void }) {
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    const isEditable = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
    }
    const onFocusIn = (e: FocusEvent) => { if (isEditable(e.target)) setEditing(true) }
    const onFocusOut = (e: FocusEvent) => {
      // Use related target — focusout fires before the new element gets focus
      if (!isEditable(e.relatedTarget)) setEditing(false)
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  return (
    <div
      className={clsx(
        'fixed bottom-6 right-6 z-40 flex items-stretch rounded-2xl shadow-lg shadow-blue/30',
        'bg-blue text-white text-sm font-semibold overflow-hidden transition-all duration-150',
        editing ? 'opacity-0 pointer-events-none translate-y-2' : 'opacity-100 pointer-events-auto translate-y-0',
      )}
      role="group"
      aria-label="Help and Quick Trade"
      aria-hidden={editing}
    >
      <button
        type="button"
        onClick={onHelp}
        className="flex items-center justify-center px-3 py-3 hover:bg-blue-light transition-colors
                   focus:outline-none focus-visible:bg-blue-light"
        aria-label="Open help (? or F1)"
        title="Help (? or F1)"
      >
        <HelpCircle size={16} aria-hidden />
      </button>
      <span className="w-px bg-white/30" aria-hidden />
      <button
        type="button"
        onClick={onTrade}
        className="flex items-center gap-2 px-4 py-3 hover:bg-blue-light transition-colors
                   focus:outline-none focus-visible:bg-blue-light"
        aria-label="Open Quick Trade"
        title="Quick Trade"
      >
        <Zap size={16} aria-hidden />
        Quick Trade
      </button>
    </div>
  )
}

function LiveIndicator() {
  const connected = useRealtimeStore(s => s.connected)
  return (
    <div
      className="flex items-center gap-1.5 text-xs"
      aria-live="polite"
      aria-label={connected ? 'Live feed connected' : 'Live feed disconnected'}
    >
      {connected
        ? <><Wifi size={12} className="text-emerald" aria-hidden /><span className="text-emerald">Live</span></>
        : <><WifiOff size={12} className="text-text-muted" aria-hidden /><span className="text-text-muted">Offline</span></>
      }
    </div>
  )
}

export default function Shell() {
  useRealtime()
  useNotificationPolling()

  // Phase B — load capability manifest once for tier-aware UI gating
  // (SideNav / disclaimers / settings panels all read from this).
  const fetchCapabilities = useCapabilities((s) => s.fetch)
  useEffect(() => {
    void fetchCapabilities()
  }, [fetchCapabilities])

  // Onboarding wizard disabled — platform is internal-only for Simon & Unman.
  // Mark onboarding complete on every mount so no one ever sees the wizard.
  const navigate = useNavigate()
  const location = useLocation()
  const mustChange = useAuth(s => s.user?.must_change_password === true)
  useEffect(() => {
    if (!localStorage.getItem('av-onboarding-complete')) {
      localStorage.setItem('av-onboarding-complete', 'true')
    }
  }, [navigate])

  // Force users with a one-time temp password to change it before they can
  // use anything else. They stay pinned on /app/change-password until the
  // must_change_password flag is cleared (happens when the change-password
  // endpoint returns 200). /logout is always reachable via the SideNav.
  useEffect(() => {
    if (mustChange && location.pathname !== '/app/change-password') {
      navigate('/app/change-password', { replace: true })
    }
  }, [mustChange, location.pathname, navigate])
  const [notifOpen, setNotifOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [quickTradeOpen, setQuickTradeOpen] = useState(false)

  // Global keyboard shortcuts
  const handleGlobalKey = useCallback((e: KeyboardEvent) => {
    const ctrl = e.metaKey || e.ctrlKey
    const tag = (e.target as HTMLElement)?.tagName
    const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable

    // Ctrl+K / Cmd+K → command palette
    if (ctrl && e.key === 'k') {
      e.preventDefault()
      setPaletteOpen(o => !o)
      return
    }

    // Navigation shortcuts (Ctrl+key)
    if (ctrl) {
      if (e.key === '/' || e.key === 't') { e.preventDefault(); navigate('/app/terminal'); return }
      if (e.key === 'p') { e.preventDefault(); navigate('/app/portfolio'); return }
      if (e.key === 'm') { e.preventDefault(); navigate('/app/indices'); return }
      if (e.key === 'i') { e.preventDefault(); navigate('/app/intelligence'); return }
      if (e.key === 'b') { e.preventDefault(); navigate('/app/advisory'); return }
    }

    // Escape → close any open overlay
    if (e.key === 'Escape') {
      if (notifOpen) { setNotifOpen(false); return }
      if (paletteOpen) { setPaletteOpen(false); return }
      if (helpOpen) { setHelpOpen(false); return }
    }

    // ? or F1 → help panel (not when typing)
    if (!isEditable && (e.key === '?' || e.key === 'F1')) {
      e.preventDefault()
      setHelpOpen(o => !o)
    }
  }, [navigate, notifOpen, paletteOpen, helpOpen])

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKey)
    return () => document.removeEventListener('keydown', handleGlobalKey)
  }, [handleGlobalKey])

  return (
    <div className="flex flex-col min-h-screen bg-base">
      {/* Live price ticker — spans full width above everything */}
      <PriceTicker />

      {/* Market clock bar — London / New York / Tokyo with open/closed status */}
      <MarketClockBar />

      <div className="flex flex-1 min-h-0">
      <SideNav />

      <div className="flex flex-col flex-1 min-w-0">
        {/* Mobile top nav */}
        <TopNav onSearchClick={() => setPaletteOpen(true)} onNotifClick={() => setNotifOpen(true)} />

        {/* Desktop top bar */}
        <div className="glass hidden md:flex items-center justify-between px-6 py-2 border-0 border-b border-border">
          <div className="flex items-center gap-4">
            <LivePortfolioTicker />
            {/* Search trigger */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-surface-card
                         text-xs text-text-muted hover:text-text-secondary hover:border-blue/30 transition-colors
                         focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-1 focus-visible:ring-offset-surface-DEFAULT"
              aria-label="Open search"
            >
              <Search size={13} aria-hidden />
              <span>Search or</span>
              <kbd className="px-1 py-0.5 rounded bg-surface-DEFAULT border border-border font-mono text-[10px]">Ctrl+K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <MarketSessionIndicator />
            <FreshnessIndicator />
            <LiveIndicator />
            <ThemeToggle />
            <NotificationDropdown onViewAll={() => setNotifOpen(true)} />
          </div>
        </div>

        {/* Page content */}
        <main
          className="flex-1 overflow-y-auto px-4 md:px-6 py-5 md:py-6"
          id="main-content"
          tabIndex={-1}
        >
          <ErrorBoundary>
            {/* ONE route-level Suspense boundary — every lazy route falls back
                to the shared skeleton PageLoader (GUI makeover item 14). */}
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      {quickTradeOpen && <QuickTradeDialog onClose={() => setQuickTradeOpen(false)} />}
      {/* Phase B regulatory onboarding gate — non-owner tiers only.
          Self-hides server-side via /api/disclaimers/status. */}
      <OnboardingDisclaimerModal />
      {!helpOpen && !quickTradeOpen && (
        <SplitFAB
          onHelp={() => setHelpOpen(true)}
          onTrade={() => setQuickTradeOpen(true)}
        />
      )}
      </div>{/* end flex row */}
    </div>
  )
}
